import { useState } from "react";
import { useGetCrmMasterDetail, type CrmMasterDetail } from "@/lib/api/crm/masters";
import { useCreateCrmRecordWithDetails } from "@/lib/api/crm/records";
import { useGetCrmRules, type CrmRules } from "@/lib/api/crm/rules";
import type { CreateCrmRecordWithDetailsResult } from "@/lib/api/crm/types";
import { CreateCrmRecordWithDetailsRequestValidation } from "@/lib/validation/crm";
import {
  genericFormSubmitError,
  issuesToFormSubmitError,
  toFormSubmitError,
  type FormSubmitError,
} from "./recordFormErrors";
import type { RecordFormValues } from "./recordFormSchema";
import { toCreateRequest } from "./recordFormValues";

export type RecordCreatorOptions = {
  /** Locks the Record Group to this one; null lets the user choose. */
  presetGroupId: string | null;
  onCreated: (result: CreateCrmRecordWithDetailsResult) => void;
  /** A caller's own check before saving; an error stops the save. */
  validate?: (rules: CrmRules, values: RecordFormValues) => FormSubmitError | null;
};

export type RecordCreatorState =
  | { status: "loading" }
  | { status: "error"; subject: "form" | "record group"; isRetrying: boolean; retry: () => void }
  | {
    status: "ready";
    rules: CrmRules;
    /** The chosen (or preset) group, once loaded. */
    group: CrmMasterDetail | undefined;
    onGroupChange: (masterId: string) => void;
    onSubmit: (values: RecordFormValues) => Promise<FormSubmitError | null>;
  };

/** Loads the rules and the chosen group for the create form; saves with create-with-details. */
export function useRecordCreator({ presetGroupId, onCreated, validate }: RecordCreatorOptions): RecordCreatorState {
  const [pickedGroupId, setPickedGroupId] = useState("");
  const groupId = presetGroupId ?? pickedGroupId;

  const rulesResult = useGetCrmRules();
  const groupResult = useGetCrmMasterDetail({ master_id: groupId }, groupId !== "");
  const createMutation = useCreateCrmRecordWithDetails();

  const rules = rulesResult.data;
  // The detail query keeps the previous group's data while the next one loads; never offer its records.
  const group = groupResult.data?.id === groupId ? groupResult.data : undefined;

  const isLoading = rulesResult.isLoading || (presetGroupId !== null && groupResult.isLoading);
  if (isLoading) return { status: "loading" };
  if (!rules || (presetGroupId !== null && !group)) {
    return {
      status: "error",
      subject: rules ? "record group" : "form",
      isRetrying: rulesResult.isFetching || groupResult.isFetching,
      retry: () => {
        if (!rules) void rulesResult.refetch();
        if (presetGroupId !== null && !group) void groupResult.refetch();
      },
    };
  }

  const onSubmit = async (values: RecordFormValues): Promise<FormSubmitError | null> => {
    const invalid = validate?.(rules, values);
    if (invalid) return invalid;
    const built = toCreateRequest(rules, values);
    if (!built) return genericFormSubmitError;
    const parsed = CreateCrmRecordWithDetailsRequestValidation.safeParse(built.request);
    if (!parsed.success) return issuesToFormSubmitError(parsed.error.issues, built.rowIndexMap);

    const result = await createMutation.mutateAsync(parsed.data);
    if (!result.success) return toFormSubmitError(rules, result, values, built.rowIndexMap);
    onCreated(result.data);
    return null;
  };

  return { status: "ready", rules, group, onGroupChange: setPickedGroupId, onSubmit };
}
